import React, { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import api from '../../utils/api';
import { getApiErrorMessage } from '../../utils/errorMessage';

const AdminInvoiceSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await api.get('/admin/invoice-settings');
        setSettings(res.data);
      } catch (error) {
        toast.error(getApiErrorMessage(error, 'Failed to load invoice settings'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const update = (path, value) => {
    setSettings((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let ref = next;
      for (let i = 0; i < keys.length - 1; i += 1) ref = ref[keys[i]];
      ref[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const save = async () => {
    try {
      setSaving(true);
      await api.put('/admin/invoice-settings', settings);
      toast.success('Invoice settings saved');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to save settings'));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !settings) {
    return <div className="text-amber-700">Loading invoice settings...</div>;
  }

  const visibility = settings.visibility || {};
  const company = settings.company || {};
  const legal = settings.legal || {};
  const bank = company.bank_details || {};

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-amber-950">Invoice Settings</h1>
        <p className="text-amber-700 mt-1">Real-time invoice layout controls. New invoices will use these settings immediately.</p>
      </div>

      <div className="bg-white border-2 border-amber-200 rounded-2xl p-6 space-y-4">
        <h2 className="text-lg font-semibold text-amber-950">Business Details</h2>
        <Input value={company.name || ''} onChange={(e) => update('company.name', e.target.value)} placeholder="Company Name" />
        <Input value={company.tagline || ''} onChange={(e) => update('company.tagline', e.target.value)} placeholder="Tagline" />
        <Input value={company.address || ''} onChange={(e) => update('company.address', e.target.value)} placeholder="Business Address" />
        <Input value={(company.gstins || []).join(', ')} onChange={(e) => update('company.gstins', e.target.value.split(',').map((x) => x.trim()).filter(Boolean))} placeholder="GSTINs (comma separated)" />
        <Input value={company.contact_email || ''} onChange={(e) => update('company.contact_email', e.target.value)} placeholder="Contact Email" />
        <Input value={company.contact_phone || ''} onChange={(e) => update('company.contact_phone', e.target.value)} placeholder="Contact Phone" />
      </div>

      <div className="bg-white border-2 border-amber-200 rounded-2xl p-6 space-y-4">
        <h2 className="text-lg font-semibold text-amber-950">Bank Details</h2>
        <Input value={bank.account_name || ''} onChange={(e) => update('company.bank_details.account_name', e.target.value)} placeholder="Account Name" />
        <Input value={bank.account_number || ''} onChange={(e) => update('company.bank_details.account_number', e.target.value)} placeholder="Account Number" />
        <Input value={bank.ifsc || ''} onChange={(e) => update('company.bank_details.ifsc', e.target.value)} placeholder="IFSC" />
        <Input value={bank.bank_name || ''} onChange={(e) => update('company.bank_details.bank_name', e.target.value)} placeholder="Bank Name" />
        <Input value={bank.branch || ''} onChange={(e) => update('company.bank_details.branch', e.target.value)} placeholder="Branch" />
        <Input value={bank.upi_id || ''} onChange={(e) => update('company.bank_details.upi_id', e.target.value)} placeholder="UPI ID" />
      </div>

      <div className="bg-white border-2 border-amber-200 rounded-2xl p-6 space-y-2">
        <h2 className="text-lg font-semibold text-amber-950 mb-2">Component Visibility</h2>
        {Object.keys(visibility).map((key) => (
          <label key={key} className="flex items-center gap-2 text-sm text-amber-900">
            <input
              type="checkbox"
              checked={Boolean(visibility[key])}
              onChange={(e) => update(`visibility.${key}`, e.target.checked)}
            />
            {key}
          </label>
        ))}
      </div>

      <div className="bg-white border-2 border-amber-200 rounded-2xl p-6 space-y-4">
        <h2 className="text-lg font-semibold text-amber-950">Legal & Footer</h2>
        <Input value={legal.no_return_policy || ''} onChange={(e) => update('legal.no_return_policy', e.target.value)} placeholder="No Return Policy Text" />
        <textarea
          rows={4}
          className="w-full border-2 border-amber-200 rounded-xl p-3"
          value={(legal.footer_notes || []).join('\n')}
          onChange={(e) => update('legal.footer_notes', e.target.value.split('\n').map((x) => x.trim()).filter(Boolean))}
          placeholder="Footer notes (one per line)"
        />
      </div>

      <Button onClick={save} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-white">
        {saving ? 'Saving...' : 'Save Invoice Settings'}
      </Button>
    </div>
  );
};

export default AdminInvoiceSettings;
